import React, { useCallback, useMemo, useState, useEffect } from "react";
import { submitTip } from "../../general/AppHelpers";
import { paymentFailedStatuses } from "../../general/constants";
import { useTranslation } from "react-i18next";
import Currency from "../../general/Currency";

const TipAdjustment = ({
  ticketData,
  accessToken,
  appointmentId,
  adjustments,
  submittingTip,
  setSubmittingTip,
}) => {
  const { t } = useTranslation();

  const [tipValue, setTipValue] = useState(0);
  const [showTipInput, setShowTipInput] = useState(false);

  const alreadyProcessedPaymentItem = useMemo(() => {
    return ticketData?.resource?.payment_items?.find(
      (item) =>
        item?.payment?.access_token === accessToken &&
        !paymentFailedStatuses.includes(item?.payment?.status)
    );
  }, [accessToken, ticketData]);

  const tip = useMemo(() => {
    return adjustments.find(
      (adj) =>
        adj.adjustable_type === "Tip" &&
        adj.adjustable?.my_client_appointment_id === appointmentId
    );
  }, [adjustments, appointmentId]);

  const disabled = useMemo(
    () =>
      submittingTip ||
      !!alreadyProcessedPaymentItem ||
      ticketData.resource.status === 1 ||
      !ticketData.resource.tips_enabled,
    [submittingTip, alreadyProcessedPaymentItem, ticketData]
  );

  useEffect(() => {
    if (tip) {
      setTipValue(tip?.amount || 0);
    }
  }, [tip]);

  const handleSubmitTip = useCallback(async () => {
    setSubmittingTip(true);

    await submitTip(accessToken, appointmentId, tipValue);

    setShowTipInput(false);
    setSubmittingTip(false);
  }, [accessToken, appointmentId, tipValue, setSubmittingTip]);

  if (!appointmentId || !ticketData.resource.tips_enabled) {
    return null;
  }

  if (tip?.amount > 0) {
    return (
      <div
        className="flex w-full justify-between items-center text-sm text-black-light"
        data-testid="tip-adjustment-existing"
      >
        <div className=" pl-8 pr-3">{t("tips.tip")}</div>
        <div className="whitespace-nowrap px-3">
          {showTipInput ? (
            <div className="flex items-center" data-testid="tip-edit-form">
              <input
                type="number"
                min="0"
                step="1"
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                value={tipValue}
                onChange={(e) => setTipValue(e.target.value)}
                placeholder="0.00"
                disabled={disabled}
                data-testid="tip-input"
              />
              <button
                className="ml-2 px-2 py-1 bg-primary text-white text-xs rounded hover:bg-primary-hover disabled:opacity-50"
                onClick={handleSubmitTip}
                disabled={disabled || tip?.amount == tipValue}
                data-testid="save-tip-button"
              >
                {submittingTip ? "Saving..." : "Save"}
              </button>
              <button
                className="ml-1 px-2 py-1 border border-gray-300 text-xs rounded hover:bg-gray-100"
                onClick={() => setShowTipInput(!showTipInput)}
                disabled={disabled}
                data-testid="cancel-tip-button"
              >
                {t("general.cancel")}
              </button>
            </div>
          ) : (
            <>
              <Currency value={tip?.amount} />
              <button
                onClick={() => setShowTipInput(!showTipInput)}
                className="ml-2 text-medium text-primary hover:text-primary-hover disabled:opacity-50"
                disabled={disabled}
                data-testid="edit-tip-button"
              >
                {t("general.edit")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex w-full justify-between items-center"
      data-testid="tip-adjustment-new"
    >
      <div className="pl-8 pr-3 text-sm text-black-light">{t("tips.tip")}</div>
      <div className="whitespace-nowrap px-3">
        {showTipInput &&
        !alreadyProcessedPaymentItem &&
        ticketData.resource.tips_enabled ? (
          <div className="flex items-center" data-testid="tip-add-form">
            <input
              type="number"
              min="0"
              step="1"
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
              value={tipValue}
              onChange={(e) => setTipValue(e.target.value)}
              placeholder="0.00"
              disabled={disabled}
              data-testid="tip-input"
            />
            <button
              className="ml-2 px-2 py-1 bg-primary text-white text-xs rounded hover:bg-primary-hover disabled:opacity-50"
              onClick={handleSubmitTip}
              disabled={disabled}
              data-testid="add-tip-button"
            >
              {submittingTip ? "Adding..." : "Add"}
            </button>
            <button
              className="ml-1 px-2 py-1 border border-gray-300 text-xs rounded hover:bg-gray-100"
              onClick={() => setShowTipInput(!showTipInput)}
              disabled={disabled}
              data-testid="cancel-tip-button"
            >
              {t("general.cancel")}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowTipInput(!showTipInput)}
            className="text-sm text-primary hover:text-primary-hover disabled:opacity-50"
            disabled={disabled}
            data-testid="show-tip-input-button"
          >
            {`+ ${t("tips.add")}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default TipAdjustment;
